/**
 * Base class for streaming pipelines that process parts one at a time.
 * Used for model-to-client and model-to-firestore transformations.
 */

import type { CoreLogger } from '@elara/core/logger';

import type { MessagePart } from '#schemas/message-schemas.js';
import type { PartProcessor, PartResult } from '../parts/index.js';

import { EventEmitter } from 'eventemitter3';

import { WidgetMarkerInterleaver } from '../parts/WidgetMarkerInterleaver.js';

export interface StreamingPipelineOptions {
  interleaveWidgets?: boolean;
}

export interface StreamEvents {
  part: [part: MessagePart];
}

export abstract class StreamingPipeline extends EventEmitter<StreamEvents> {
  protected abstract readonly partProcessor: PartProcessor;
  protected abstract readonly pipelineId: string;

  protected readonly widgetInterleaver: WidgetMarkerInterleaver | undefined;

  constructor(
    protected readonly baseLogger: CoreLogger,
    options: StreamingPipelineOptions = {},
  ) {
    super();
    this.widgetInterleaver = options.interleaveWidgets ? new WidgetMarkerInterleaver() : undefined;
  }

  protected _logger: CoreLogger | undefined;

  protected get logger(): CoreLogger {
    this._logger ??= this.baseLogger.child({ name: this.pipelineId });
    return this._logger!;
  }

  async processPart(part: MessagePart, context: { meta?: Record<string, unknown> } = {}): Promise<PartResult> {
    const result = await this.partProcessor.process(part, {
      ...context,
      pipeline: this.pipelineId,
      logger: this.logger,
    });
    return this.#emitParts(result);
  }

  processPartSync(part: MessagePart, context: { meta?: Record<string, unknown> } = {}): PartResult {
    const result = this.partProcessor.processSync(part, { ...context, pipeline: this.pipelineId, logger: this.logger });
    return this.#emitParts(result);
  }

  flush(): MessagePart[] {
    const parts = this.widgetInterleaver?.flush(this.logger) ?? [];
    for (const part of parts) {
      this.#emit('part', part);
    }
    return parts;
  }

  #emitParts(result: PartResult): PartResult {
    const parts = this.widgetInterleaver
      ? result.parts.flatMap((part) => this.widgetInterleaver!.push(part))
      : result.parts;
    for (const part of parts) {
      this.#emit('part', part);
    }
    return parts === result.parts ? result : { ...result, parts };
  }

  #pendingHandlers: Array<Promise<void>> = [];

  /**
   * Emits an event and tracks any Promises returned by async handlers.
   * This ensures we can wait for all handlers to complete before calling persist().
   */
  #emit<E extends keyof StreamEvents>(event: E, ...args: StreamEvents[E]): void {
    const listeners = this.listeners(event) as Array<(...args: StreamEvents[E]) => void | Promise<void>>;
    for (const listener of listeners) {
      try {
        const result = listener(...args);
        if (result instanceof Promise) {
          // Track the Promise and handle any errors
          this.#pendingHandlers.push(
            result.catch((err) => {
              this.logger.error({ err, event }, `Async event handler for '${event}' failed`);
            }),
          );
        }
      } catch (err) {
        // Handle synchronous errors in handlers
        this.logger.error({ err, event }, `Sync event handler for '${event}' threw`);
      }
    }
  }

  /**
   * Waits for all pending async event handlers to complete.
   * Should be called before returning the result of the pipeline.
   */
  async waitForPendingHandlers(): Promise<void> {
    if (this.#pendingHandlers.length > 0) {
      this.logger.debug({ count: this.#pendingHandlers.length }, 'Waiting for pending async handlers');
      await Promise.all(this.#pendingHandlers);
      this.logger.debug({ count: this.#pendingHandlers.length }, 'All pending async handlers completed');
      this.#pendingHandlers = [];
    }
  }
}
